import { useState, useEffect, useMemo } from "react";
import { ref, onValue, get } from "firebase/database";
import { getFunctions, httpsCallable } from "firebase/functions";
import { database } from "../constants/firebase";
import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { ICONS } from "../constants/icons";
import {
  LuDollarSign,
  LuClock,
  LuCheck,
  LuShieldCheck,
  LuShieldAlert,
  LuSearch,
  LuX,
} from "react-icons/lu";

const FILTERS = [
  { key: "pending", label: "Pending" },
  { key: "paid", label: "Paid" },
  { key: "all", label: "All" },
];

const fmt = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

const AdminPayouts = () => {
  const { currentUser } = useUser();
  const navigate = useMoveTo();

  const [payouts, setPayouts] = useState([]);
  const [names, setNames] = useState({}); // uid -> display name
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");

  // Mark-as-paid modal state
  const [activePayout, setActivePayout] = useState(null);
  const [paymentReference, setPaymentReference] = useState("");
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState({ type: "", message: "" });

  useEffect(() => {
    const payoutsRef = ref(database, "ProductPayouts");
    const unsub = onValue(payoutsRef, (snap) => {
      const val = snap.val() || {};
      const list = Object.entries(val).map(([id, p]) => ({ id, ...p }));
      list.sort((a, b) =>
        String(b.createdAt || "").localeCompare(String(a.createdAt || "")),
      );
      setPayouts(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Lazily resolve seller/buyer display names.
  useEffect(() => {
    const ids = new Set();
    payouts.forEach((p) => {
      if (p.sellerId) ids.add(p.sellerId);
      if (p.buyerId) ids.add(p.buyerId);
    });
    const missing = [...ids].filter((id) => names[id] === undefined);
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      await Promise.all(
        missing.map(async (id) => {
          try {
            const s = await get(ref(database, `Users/${id}/name`));
            updates[id] = s.val() || id;
          } catch {
            updates[id] = id;
          }
        }),
      );
      if (!cancelled) setNames((prev) => ({ ...prev, ...updates }));
    })();
    return () => {
      cancelled = true;
    };
  }, [payouts, names]);

  const stats = useMemo(() => {
    let pendingCount = 0;
    let pendingAmount = 0;
    let paidAmount = 0;
    let unverified = 0;
    payouts.forEach((p) => {
      if (p.status === "paid") {
        paidAmount += Number(p.sellerPayout) || 0;
      } else {
        pendingCount += 1;
        pendingAmount += Number(p.sellerPayout) || 0;
      }
      if (p.verified === false && p.status !== "paid") unverified += 1;
    });
    return { pendingCount, pendingAmount, paidAmount, unverified };
  }, [payouts]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payouts.filter((p) => {
      if (filter === "pending" && p.status === "paid") return false;
      if (filter === "paid" && p.status !== "paid") return false;
      if (!q) return true;
      const hay = [
        p.productTitle,
        p.sellerId,
        names[p.sellerId],
        p.buyerId,
        names[p.buyerId],
        p.transactionId,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [payouts, filter, search, names]);

  const openMarkPaid = (p) => {
    setActivePayout(p);
    setPaymentReference("");
    setToast({ type: "", message: "" });
  };

  const confirmMarkPaid = async () => {
    if (!activePayout) return;
    setProcessing(true);
    try {
      const fns = getFunctions();
      const markPaid = httpsCallable(fns, "markPayoutPaid");
      await markPaid({
        payoutId: activePayout.id,
        paymentReference: paymentReference.trim() || null,
      });
      setActivePayout(null);
      setToast({ type: "success", message: "Payout marked as paid." });
    } catch (err) {
      setToast({
        type: "error",
        message: err?.message || "Failed to mark payout as paid.",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (!currentUser?.isAdmin) {
    return (
      <div className="h-screen flex items-center justify-center bg-white dark:bg-[#1A1A1A]">
        <p className="text-gray-500">Access Denied</p>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-[#1A1A1A] text-black dark:text-white">
      <MobileHeader title={"Payouts"} isBackBtn={true} />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 min-h-0 overflow-y-auto pt-5 pb-24 md:pb-20 px-4 md:px-6">
          <div className="flex items-center space-x-4 mb-6">
            <button onClick={() => navigate(-1)} className="hidden md:block">
              <img
                src={ICONS.arrowBack}
                className="w-5 h-5 grayscale brightness-0 dark:invert"
                alt="Back"
              />
            </button>
            <h2 className="text-2xl md:text-3xl font-bold">Seller Payouts</h2>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white dark:bg-[#1f1f1f] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
              <div className="flex items-center text-amber-500 mb-2">
                <LuClock className="mr-2" />
                <span className="text-xs font-bold uppercase tracking-widest">
                  Pending
                </span>
              </div>
              <p className="text-2xl font-extrabold">{fmt(stats.pendingAmount)}</p>
              <p className="text-xs text-gray-400 mt-1">
                {stats.pendingCount} payout{stats.pendingCount === 1 ? "" : "s"} owed
              </p>
            </div>
            <div className="bg-white dark:bg-[#1f1f1f] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
              <div className="flex items-center text-emerald-500 mb-2">
                <LuCheck className="mr-2" />
                <span className="text-xs font-bold uppercase tracking-widest">
                  Paid out
                </span>
              </div>
              <p className="text-2xl font-extrabold">{fmt(stats.paidAmount)}</p>
              <p className="text-xs text-gray-400 mt-1">Total settled to sellers</p>
            </div>
            <div className="bg-white dark:bg-[#1f1f1f] p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
              <div className="flex items-center text-red-500 mb-2">
                <LuShieldAlert className="mr-2" />
                <span className="text-xs font-bold uppercase tracking-widest">
                  Unverified
                </span>
              </div>
              <p className="text-2xl font-extrabold">{stats.unverified}</p>
              <p className="text-xs text-gray-400 mt-1">
                Pending sales not receipt-verified
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="flex bg-gray-100 dark:bg-[#252525] rounded-xl p-1">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${
                    filter === f.key
                      ? "bg-[#327691] text-white shadow"
                      : "text-gray-500 hover:text-black dark:hover:text-white"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="relative flex-1 max-w-sm">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search product, seller, buyer…"
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-[#252525] border-none rounded-xl text-sm focus:ring-2 focus:ring-[#327691] outline-none"
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-[#1f1f1f] rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#327691] border-t-transparent" />
              </div>
            ) : visible.length === 0 ? (
              <div className="p-12 text-center text-gray-400 text-sm">
                No payouts to show.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
                      <th className="px-4 py-3 font-bold">Product</th>
                      <th className="px-4 py-3 font-bold">Seller</th>
                      <th className="px-4 py-3 font-bold">Buyer</th>
                      <th className="px-4 py-3 font-bold text-right">Tier</th>
                      <th className="px-4 py-3 font-bold text-right">Payout</th>
                      <th className="px-4 py-3 font-bold">Date</th>
                      <th className="px-4 py-3 font-bold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((p) => {
                      const isPaid = p.status === "paid";
                      return (
                        <tr
                          key={p.id}
                          className="border-b border-gray-50 dark:border-gray-800/50 hover:bg-gray-50/60 dark:hover:bg-white/5"
                        >
                          <td className="px-4 py-3">
                            <div className="font-semibold truncate max-w-[200px]">
                              {p.productTitle || p.productId}
                            </div>
                            <div className="mt-1">
                              {p.verified === false ? (
                                <span className="inline-flex items-center text-[10px] font-bold text-red-500">
                                  <LuShieldAlert className="mr-1" /> Unverified
                                </span>
                              ) : (
                                <span className="inline-flex items-center text-[10px] font-bold text-emerald-500">
                                  <LuShieldCheck className="mr-1" /> Verified
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="truncate max-w-[160px]">
                              {names[p.sellerId] || p.sellerId}
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {p.sellerIsPro ? "Pro seller" : "Free seller"}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="truncate max-w-[160px]">
                              {names[p.buyerId] || p.buyerId}
                            </div>
                            <div className="text-[10px] text-gray-400">
                              {p.buyerIsPro ? "Pro buyer" : "Free buyer"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500">
                            {fmt(p.tierPrice)}
                          </td>
                          <td className="px-4 py-3 text-right font-bold">
                            {fmt(p.sellerPayout)}
                          </td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                            {fmtDate(p.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isPaid ? (
                              <span className="inline-flex items-center text-xs font-bold text-emerald-500">
                                <LuCheck className="mr-1" /> Paid
                              </span>
                            ) : (
                              <button
                                onClick={() => openMarkPaid(p)}
                                className="px-3 py-1.5 bg-[#327691] hover:bg-[#2a637a] text-white rounded-lg text-xs font-bold transition-all"
                              >
                                Mark as paid
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {toast.message && (
            <div
              className={`mt-4 p-4 rounded-xl text-sm font-medium ${
                toast.type === "success"
                  ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400"
                  : "bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400"
              }`}
            >
              {toast.message}
            </div>
          )}
        </main>
      </div>

      {/* Mark-as-paid modal */}
      {activePayout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-[#1f1f1f] rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-xl bg-[#327691]/10 flex items-center justify-center mr-3">
                  <LuDollarSign className="text-[#327691] text-xl" />
                </div>
                <div>
                  <h3 className="font-bold text-lg leading-tight">
                    Mark payout as paid
                  </h3>
                  <p className="text-xs text-gray-400">
                    {activePayout.productTitle}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setActivePayout(null)}
                className="text-gray-400 hover:text-black dark:hover:text-white"
              >
                <LuX />
              </button>
            </div>

            <div className="bg-gray-50 dark:bg-[#252525] rounded-xl p-4 mb-4 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Seller</span>
                <span className="font-semibold">
                  {names[activePayout.sellerId] || activePayout.sellerId}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Amount to pay</span>
                <span className="font-extrabold text-[#327691]">
                  {fmt(activePayout.sellerPayout)}
                </span>
              </div>
              {activePayout.verified === false && (
                <div className="flex items-center text-xs text-red-500 pt-1">
                  <LuShieldAlert className="mr-1" /> This sale was not
                  receipt-verified — confirm it before paying.
                </div>
              )}
            </div>

            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 block">
              Payment reference (optional)
            </label>
            <input
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="e.g. bank transfer ref / PayPal id"
              className="w-full px-4 py-3 bg-gray-50 dark:bg-[#252525] border-none rounded-xl text-sm focus:ring-2 focus:ring-[#327691] outline-none mb-5"
            />

            <div className="flex gap-3">
              <button
                onClick={() => setActivePayout(null)}
                disabled={processing}
                className="flex-1 py-3 rounded-xl font-bold border border-gray-200 dark:border-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmMarkPaid}
                disabled={processing}
                className="flex-1 py-3 bg-[#327691] hover:bg-[#2a637a] text-white rounded-xl font-bold flex items-center justify-center disabled:opacity-50"
              >
                {processing ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  "Confirm paid"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayouts;
